import React, { HTMLAttributes, ReactNode } from 'react';

import {
  FieldValues,
  Path,
  PathValue,
  UseControllerProps,
  useController,
} from 'react-hook-form';
import {
  TextField,
  Autocomplete,
  AutocompleteProps,
  TextFieldProps,
  AutocompleteRenderOptionState,
  InputBaseProps,
} from '@mui/material';
import { VirtualizedList } from '../VirtualizedList/VirtualizedList';
import { useAutocompleteOneValue } from '../hooks/useAutocompleteOneValue';

type ComboBoxProps<
  T extends FieldValues,
  TName extends Path<T>,
  TOption extends Record<string, string | number>,
  TDisableClearable extends boolean | undefined,
> = {
  options: TOption[];
  valueKey?: keyof TOption;
  labelKey?: keyof TOption;
  dataTestId?: string;
  InputProps?: InputBaseProps;
  renderOptionLabel?: (option: TOption) => ReactNode;
  getOptionDisabled?: (option: TOption) => boolean;
} & UseControllerProps<T, TName> &
  Pick<
    AutocompleteProps<TOption, true, TDisableClearable, false>,
    'disableClearable' | 'disabled' | 'loading' | 'className' | 'noOptionsText'
  > &
  Pick<TextFieldProps, 'label' | 'variant' | 'size' | 'margin'>;

const ComboBox = <
  T extends FieldValues,
  TName extends Path<T>,
  TOption extends Record<string, string | number>,
  TDisableClearable extends boolean | undefined,
>({
  control,
  name,
  rules,
  defaultValue = null as PathValue<T, TName>,
  options: userOptions,
  label,
  shouldUnregister,
  valueKey = 'value',
  labelKey = 'label',
  size,
  disabled,
  disableClearable = true,
  variant,
  dataTestId,
  loading,
  margin,
  className,
  noOptionsText,
  InputProps,
  renderOptionLabel,
  getOptionDisabled,
}: ComboBoxProps<T, TName, TOption, TDisableClearable>) => {
  const {
    field: { ref, ...field },
    fieldState: { error },
  } = useController({
    control,
    name,
    rules,
    defaultValue,
    shouldUnregister,
  });

  const { options, getOptionLabel, getOption } = useAutocompleteOneValue({
    userOptions,
    valueKey,
    labelKey,
  });

  const getRenderOption = (
    props: HTMLAttributes<HTMLLIElement>,
    value: TOption[keyof TOption],
    state: AutocompleteRenderOptionState,
  ) =>
    ({
      props,
      label: renderOptionLabel
        ? renderOptionLabel(getOption(value))
        : getOptionLabel(value),
      selected: state.selected,
    }) as any;

  const processGetOptionDisabled = getOptionDisabled
    ? (value: TOption[keyof TOption]) => getOptionDisabled(getOption(value))
    : undefined;

  const isRequired = !!rules?.required;

  return (
    <Autocomplete
      {...field}
      tabIndex={-1}
      fullWidth
      size={size}
      onChange={(event, value) => field.onChange(value)}
      loading={loading}
      disabled={disabled}
      disableClearable={disableClearable}
      options={options}
      getOptionLabel={getOptionLabel} // for shown input value
      getOptionDisabled={processGetOptionDisabled}
      renderOption={getRenderOption}
      data-test-id={dataTestId}
      className={className}
      noOptionsText={noOptionsText}
      slotProps={{
        popper: { className: 'general-style variant-select', placement: 'bottom-start' },
        listbox: { component: VirtualizedList as any },
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            slotProps={{
              input: { ...params.InputProps, ...InputProps },
            }}
            helperText={error?.message}
            error={!!error}
            label={label}
            required={isRequired}
            variant={variant}
            margin={margin}
            inputRef={ref}
          />
        );
      }}
    />
  );
};

export { ComboBox };
